import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrashIcon,
  CheckIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PlusIcon,
  XMarkIcon,
  UsersIcon,
  UserGroupIcon,
  DocumentTextIcon,
  PaperClipIcon,
  ClockIcon,
  ArrowPathIcon,
  PencilIcon,
} from '@heroicons/react/24/outline'
import { notificationService } from '../../services/notificationService'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

interface Notification {
  notification_id: number
  type: string
  title: string
  message: string
  data: any
  is_read: boolean
  created_at: string
}

interface NotificationLog {
  log_id: number
  channel: string
  recipient: string
  subject?: string
  content: string
  status: string
  created_at: string
  customers?: {
    company_name?: string
    first_name?: string
    last_name?: string
  }
}

const TABS = [
  { id: 'inbox', label: 'Inbox', icon: BellIcon },
  { id: 'history', label: 'History', icon: ClockIcon },
  { id: 'compose', label: 'Compose', icon: PaperAirplaneIcon },
  { id: 'templates', label: 'Templates', icon: DocumentTextIcon },
]

export const NotificationsPage = () => {
  const [activeTab, setActiveTab] = useState('inbox')
  const [filter, setFilter] = useState<'all' | 'unread'>('all')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationService.getAll(),
  })

  const { data: smsCreditsData } = useQuery({
    queryKey: ['sms-credits'],
    queryFn: () => notificationService.getSMSCredits(),
  })

  const notifications: Notification[] = data?.data?.data || []
  const filteredNotifications = filter === 'unread'
    ? (notifications ?? []).filter(n => !n.is_read)
    : notifications
  const unreadCount = (notifications ?? []).filter(n => !n.is_read).length
  const smsCredits = smsCreditsData?.data?.data

  const markReadMutation = useMutation({
    mutationFn: (id: number) => notificationService.markAsRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('All notifications marked as read')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => notificationService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const getIcon = (type: string) => {
    switch (type) {
      case 'low_stock': return <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
      case 'expiry_warning': return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
      case 'payment_received': return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'consignment_due': return <InformationCircleIcon className="w-5 h-5 text-blue-500" />
      default: return <BellIcon className="w-5 h-5 text-gray-500" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'low_stock': return 'bg-orange-50 border-orange-200'
      case 'expiry_warning': return 'bg-red-50 border-red-200'
      case 'payment_received': return 'bg-green-50 border-green-200'
      case 'consignment_due': return 'bg-blue-50 border-blue-200'
      default: return 'bg-gray-50 border-gray-200'
    }
  }

  // SMS Credits Card Component
  const SMSCreditsCard = () => {
    const credits = smsCredits?.remaining_credits || 0
    const total = smsCredits?.total_credits || 0
    const used = smsCredits?.used_credits || 0
    const percentage = total > 0 ? (credits / total) * 100 : 0
    const isLow = credits < (smsCredits?.alert_threshold || 100)

    return (
      <div className={`rounded-xl shadow border p-4 ${isLow ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isLow ? 'bg-red-100' : 'bg-blue-100'}`}>
              <DevicePhoneMobileIcon className={`w-6 h-6 ${isLow ? 'text-red-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className={`text-sm ${isLow ? 'text-red-600' : 'text-blue-600'}`}>SMS Credits</p>
              <p className={`text-2xl font-bold ${isLow ? 'text-red-700' : 'text-blue-700'}`}>
                {credits.toLocaleString()}
              </p>
            </div>
          </div>
          {isLow && (
            <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-medium rounded-full">
              Low
            </span>
          )}
        </div>
        <div className="mt-3">
          <div className="h-2 bg-white rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${isLow ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.max(percentage, 5)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {used.toLocaleString()} used / {total.toLocaleString()} total
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage notifications, SMS credits, and communications
          </p>
        </div>
        {activeTab === 'inbox' && unreadCount > 0 && (
          <button
            onClick={() => markAllReadMutation.mutate()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <CheckIcon className="w-4 h-4" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <BellIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-lg font-semibold">{notifications.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Unread</p>
              <p className="text-lg font-semibold">{unreadCount}</p>
            </div>
          </div>
        </div>
        <SMSCreditsCard />
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <PaperAirplaneIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sent Today</p>
              <p className="text-lg font-semibold">-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-2">
        <div className="flex gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === tab.id
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'inbox' && unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-white bg-opacity-20 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'inbox' && (
        <InboxTab
          notifications={filteredNotifications}
          isLoading={isLoading}
          filter={filter}
          setFilter={setFilter}
          unreadCount={unreadCount}
          getIcon={getIcon}
          getTypeColor={getTypeColor}
          markReadMutation={markReadMutation}
          deleteMutation={deleteMutation}
        />
      )}
      {activeTab === 'history' && <HistoryTab />}
      {activeTab === 'compose' && <ComposeTab />}
      {activeTab === 'templates' && <TemplatesTab />}
    </div>
  )
}

// ==================== INBOX TAB ====================
const InboxTab = ({
  notifications,
  isLoading,
  filter,
  setFilter,
  unreadCount,
  getIcon,
  getTypeColor,
  markReadMutation,
  deleteMutation,
}: any) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (notifications.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
        <BellIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
        </h3>
        <p className="text-gray-500">
          {filter === 'unread' ? "You're all caught up!" : 'Notifications will appear here when there are important updates'}
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium ${filter === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg font-medium ${filter === 'unread' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          Unread ({unreadCount})
        </button>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {notifications.map((notification: Notification) => (
          <div
            key={notification.notification_id}
            className={`p-4 rounded-xl border ${notification.is_read
                ? 'bg-white border-gray-100'
                : `${getTypeColor(notification.type)}`
              }`}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">{getIcon(notification.type)}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">{notification.title}</h3>
                  {!notification.is_read && (
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                      New
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(notification.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {!notification.is_read && (
                  <button
                    onClick={() => markReadMutation.mutate(notification.notification_id)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    title="Mark as read"
                  >
                    <CheckIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => deleteMutation.mutate(notification.notification_id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

// ==================== HISTORY TAB ====================
const HistoryTab = () => {
  const [channel, setChannel] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['notification-history', channel, status, page],
    queryFn: () => notificationService.getHistory({ channel, status, page, limit: 20 }),
  })

  const logs: NotificationLog[] = data?.data?.data?.items || []
  const pagination = data?.data?.data?.pagination

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800'
      case 'delivered': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <EnvelopeIcon className="w-4 h-4" />
      case 'sms': return <DevicePhoneMobileIcon className="w-4 h-4" />
      default: return <BellIcon className="w-4 h-4" />
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4 flex flex-wrap gap-4">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Channels</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Status</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <ClockIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No history found</h3>
            <p className="text-gray-500">Sent notifications will appear here</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {dayjs(log.created_at).format('MMM D, YYYY HH:mm')}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">
                        {log.customers?.company_name ||
                          `${log.customers?.first_name} ${log.customers?.last_name}`.trim() ||
                          'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500">{log.recipient}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        {getChannelIcon(log.channel)}
                        <span className="capitalize">{log.channel}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {log.subject || log.content.substring(0, 50)}...
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {pagination.page} of {pagination.pages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ==================== COMPOSE TAB ====================
const ComposeTab = () => {
  const [channel, setChannel] = useState<'email' | 'sms' | 'both'>('email')
  const [recipientMode, setRecipientMode] = useState<'groups' | 'individuals'>('groups')
  const [selectedGroups, setSelectedGroups] = useState<number[]>([])
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([])
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [showPreview, setShowPreview] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const { data: recipientsData } = useQuery({
    queryKey: ['notification-recipients'],
    queryFn: () => notificationService.getRecipients(),
  })

  const { data: templatesData } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => notificationService.getTemplates(),
  })

  const sendMutation = useMutation({
    mutationFn: (data: any) => notificationService.sendBulk(data),
    onSuccess: () => {
      toast.success('Message sent successfully')
      queryClient.invalidateQueries({ queryKey: ['notification-history'] })
      queryClient.invalidateQueries({ queryKey: ['sms-credits'] })
      // Reset form
      setSelectedGroups([])
      setSelectedCustomers([])
      setSubject('')
      setBody('')
      setAttachments([])
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send message')
    },
  })

  const groups = recipientsData?.data?.data?.groups || []
  const customers = recipientsData?.data?.data?.customers || []
  const templates = templatesData?.data?.data || []

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files))
    }
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (recipientMode === 'groups' && selectedGroups.length === 0) {
      toast.error('Please select at least one group')
      return
    }
    if (recipientMode === 'individuals' && selectedCustomers.length === 0) {
      toast.error('Please select at least one customer')
      return
    }
    if (!body.trim()) {
      toast.error('Message body is required')
      return
    }
    if (channel === 'email' && !subject.trim()) {
      toast.error('Subject is required for email')
      return
    }

    const data: any = {
      channel,
      subject,
      body,
    }

    if (recipientMode === 'groups') {
      data.group_ids = selectedGroups
    } else {
      data.customer_ids = selectedCustomers
    }

    // Convert attachments to base64 if needed
    if (attachments.length > 0 && channel !== 'sms') {
      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          const buffer = await file.arrayBuffer()
          const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)))
          return {
            filename: file.name,
            content: base64,
            contentType: file.type,
          }
        })
      )
      data.attachments = attachmentData
    }

    sendMutation.mutate(data)
  }

  const recipientCount = recipientMode === 'groups'
    ? selectedGroups.reduce((sum, gid) => sum + (groups.find((g: any) => g.id === gid)?.customerCount || 0), 0)
    : selectedCustomers.length

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Compose Form */}
      <div className="lg:col-span-2 space-y-6">
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow border border-gray-100 p-6 space-y-6">
          {/* Channel Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Channel</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setChannel('email')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${channel === 'email' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <EnvelopeIcon className="w-5 h-5" />
                Email
              </button>
              <button
                type="button"
                onClick={() => setChannel('sms')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${channel === 'sms' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <DevicePhoneMobileIcon className="w-5 h-5" />
                SMS
              </button>
              <button
                type="button"
                onClick={() => setChannel('both')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${channel === 'both' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <BellIcon className="w-5 h-5" />
                Both
              </button>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipients</label>
            <div className="flex gap-3 mb-3">
              <button
                type="button"
                onClick={() => setRecipientMode('groups')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${recipientMode === 'groups' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-700'
                  }`}
              >
                <UserGroupIcon className="w-4 h-4" />
                Groups
              </button>
              <button
                type="button"
                onClick={() => setRecipientMode('individuals')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${recipientMode === 'individuals' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-700'
                  }`}
              >
                <UsersIcon className="w-4 h-4" />
                Individuals
              </button>
            </div>

            {recipientMode === 'groups' ? (
              <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                {groups.map((group: any) => (
                  <label key={group.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGroups([...selectedGroups, group.id])
                        } else {
                          setSelectedGroups(selectedGroups.filter(id => id !== group.id))
                        }
                      }}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{group.name}</p>
                      <p className="text-xs text-gray-500">{group.customerCount} customers • {group.priceTier}</p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                {customers.map((customer: any) => (
                  <label key={customer.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCustomers([...selectedCustomers, customer.id])
                        } else {
                          setSelectedCustomers(selectedCustomers.filter(id => id !== customer.id))
                        }
                      }}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.email || customer.phone} • {customer.groupName}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2">
              {recipientCount} recipient(s) selected
            </p>
          </div>

          {/* Subject (Email only) */}
          {(channel === 'email' || channel === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Enter subject..."
              />
            </div>
          )}

          {/* Message Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder={channel === 'sms' ? 'Enter SMS message...' : 'Enter message...'}
            />
            <p className="text-xs text-gray-500 mt-1">
              {body.length} characters
              {channel === 'sms' && body.length > 160 && ` (${Math.ceil(body.length / 160)} SMS)`}
            </p>
          </div>

          {/* Attachments (Email only) */}
          {(channel === 'email' || channel === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <PaperClipIcon className="w-4 h-4" />
                Add Attachment
              </button>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                      <span className="flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="text-primary-600 hover:underline text-sm"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setSelectedGroups([])
                  setSelectedCustomers([])
                  setSubject('')
                  setBody('')
                  setAttachments([])
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={sendMutation.isPending || recipientCount === 0}
                className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {sendMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-4 h-4" />
                    Send ({recipientCount})
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Templates */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Templates</h3>
          <div className="space-y-2">
            {templates.slice(0, 5).map((template: any) => (
              <button
                key={template.template_id}
                onClick={() => {
                  setSubject(template.subject || '')
                  setBody(template.body)
                }}
                className="w-full text-left p-2 hover:bg-gray-50 rounded-lg text-sm"
              >
                <p className="font-medium">{template.template_name}</p>
                <p className="text-xs text-gray-500 capitalize">{template.type}</p>
              </button>
            ))}
            {templates.length === 0 && (
              <p className="text-sm text-gray-500">No templates available</p>
            )}
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Preview</h3>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              {(channel === 'email' || channel === 'both') && (
                <div className="mb-3 pb-3 border-b border-gray-100">
                  <p className="text-xs text-gray-500">Subject</p>
                  <p className="font-medium">{subject || '(No subject)'}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 mb-1">Message</p>
                <p className="text-sm whitespace-pre-wrap">{body || '(No message)'}</p>
              </div>
              {attachments.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((file, i) => (
                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {file.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Variables Help */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Available Variables</h3>
          <div className="space-y-1 text-sm text-blue-800">
            <p><code>{'{{customer_name}}'}</code> - Customer name</p>
            <p><code>{'{{customer_email}}'}</code> - Email address</p>
            <p><code>{'{{customer_phone}}'}</code> - Phone number</p>
            <p><code>{'{{total_due}}'}</code> - Total amount due</p>
            <p><code>{'{{invoice_number}}'}</code> - Invoice number</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ==================== TEMPLATES TAB ====================
const TemplatesTab = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<any>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => notificationService.getTemplates(),
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => notificationService.createTemplate(data),
    onSuccess: () => {
      toast.success('Template created')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
      setShowModal(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => notificationService.updateTemplate(id, data),
    onSuccess: () => {
      toast.success('Template updated')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
      setShowModal(false)
      setEditingTemplate(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => notificationService.deleteTemplate(id),
    onSuccess: () => {
      toast.success('Template deleted')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
    },
  })

  const templates = data?.data?.data || []

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      template_name: formData.get('template_name'),
      template_code: formData.get('template_code'),
      type: formData.get('type'),
      subject: formData.get('subject'),
      body: formData.get('body'),
    }

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.template_id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="w-4 h-4" />
          New Template
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No templates</h3>
          <p className="text-gray-500 mb-4">Create templates for common messages</p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4" />
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template: any) => (
            <div key={template.template_id} className="bg-white rounded-xl shadow border border-gray-100 p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{template.template_name}</h3>
                  <p className="text-xs text-gray-500">{template.template_code} • {template.type}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setEditingTemplate(template)
                      setShowModal(true)
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(template.template_id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {template.subject && (
                <p className="text-sm text-gray-600 mb-1">Subject: {template.subject}</p>
              )}
              <p className="text-sm text-gray-600 line-clamp-3">{template.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-semibold mb-4">
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input
                    name="template_name"
                    defaultValue={editingTemplate?.template_name}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Code</label>
                  <input
                    name="template_code"
                    defaultValue={editingTemplate?.template_code}
                    required
                    className="w-full px-3 py-2 border rounded-lg uppercase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select name="type" defaultValue={editingTemplate?.type || 'email'} className="w-full px-3 py-2 border rounded-lg">
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subject (optional)</label>
                  <input
                    name="subject"
                    defaultValue={editingTemplate?.subject}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Body</label>
                  <textarea
                    name="body"
                    defaultValue={editingTemplate?.body}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg"
                  >
                    {editingTemplate ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
